import {
	IInitialFormData,
	TEditGameSelectedItem,
	TEditGameVisibleForm,
	TSourceUrlLocally,
	TTypeOfEditGameModalWindow,
} from './editGameContext';
import {
	IItemForGetShortInfo,
	TFileId,
	TLanguageIDs,
	IFileData,
	IServerError,
	IGameApplicationIdValidationError,
	IEditGame,
	IGameTranslation,
} from 'types';

export enum EditGameActionTypes {
	REFETCH_DATA = 'REFETCH_DATA',
	SET_INITIAL_FORM_DATA = 'SET_INITIAL_FORM_DATA',
	SET_ALL_DATA = 'SET_ALL_DATA',
	CHANGE_VISIBLE_FORM = 'CHANGE_VISIBLE_FORM',
	SET_FILE = 'SET_FILE',
	DELETE_PHOTO = 'DELETE_PHOTO',
	SET_FETCHED_DEVELOPERS = 'SET_FETCHED_DEVELOPERS',
	SET_ID_DEVELOPER = 'SET_ID_DEVELOPER',
	REFETCH_DEVELOPERS = 'REFETCH_DEVELOPERS',
	SET_FETCHED_CATEGORIES = 'SET_FETCHED_CATEGORIES',
	SET_ID_CATEGORY = 'SET_ID_CATEGORY',
	CHANGE_APPLICATION_ID = 'CHANGE_APPLICATION_ID',
	CHANGE_NAME = 'CHANGE_NAME',
	CHANGE_VERSION_CODE = 'CHANGE_VERSION_CODE',
	CHANGE_VERSION_NAME = 'CHANGE_VERSION_NAME',
	CHANGE_DESCRIPTION = 'CHANGE_DESCRIPTION',
	CHANGE_APP_STORE_LINK = 'CHANGE_APP_STORE_LINK',
	CHANGE_PLAY_MARKET_LINK = 'CHANGE_PLAY_MARKET_LINK',
	CHANGE_FILE_DATA = 'CHANGE_FILE_DATA',
	SET_FETCHED_LANGUAGES = 'SET_FETCHED_LANGUAGES',
	SET_IDs_LANGUAGES = 'SET_IDs_LANGUAGES',
	SET_DATA_FOR_SUBMIT = 'SET_DATA_FOR_SUBMIT',
	CHANGE_TYPE_OF_MODAL_WINDOW = 'CHANGE_TYPE_OF_MODAL_WINDOW',
	SHOW_ERROR_MODAL_WITH_INFO = 'SHOW_ERROR_MODAL_WITH_INFO',
	SET_TRANSLATION_TAB = 'SET_TRANSLATION_TAB',
	SET_TRANSLATION_FIELDS = 'SET_TRANSLATION_FIELDS',
	CHANGE_TRANSLATION = 'CHANGE_TRANSLATION',
	RESET_FIELDS = 'RESET_FIELDS',
}

interface IRefetchData {
	type: EditGameActionTypes.REFETCH_DATA;
	payload: {
		needRefetch: boolean;
	};
}

interface ISetInitialFormData {
	type: EditGameActionTypes.SET_INITIAL_FORM_DATA;
	payload: {
		initialFormData: IInitialFormData;
	};
}

interface ISetAllData {
	type: EditGameActionTypes.SET_ALL_DATA;
	payload: {
		gameID: string;
		IDSelectedDeveloper: TEditGameSelectedItem;
		IDSelectedCategory: TEditGameSelectedItem;
		applicationId: string;
		name: string;
		versionCode: string;
		versionName: string;
		description: string;
		appStoreLink: string;
		playMarketLink: string;
		IDsSelectedLanguages: TLanguageIDs[];
		translations: IGameTranslation[];
		gamePath: string;
		iconPath: string;
	};
}

interface IChangeVisibleForm {
	type: EditGameActionTypes.CHANGE_VISIBLE_FORM;
	payload: {
		visibleForm: TEditGameVisibleForm;
	};
}

interface ISetFile {
	type: EditGameActionTypes.SET_FILE;
	payload: {
		fileId: TFileId;
		sourceUrlLocally: TSourceUrlLocally;
	};
}

interface IDeletePhoto {
	type: EditGameActionTypes.DELETE_PHOTO;
}

interface ISetFetchedDevelopers {
	type: EditGameActionTypes.SET_FETCHED_DEVELOPERS;
	payload: {
		developers: IItemForGetShortInfo[];
	};
}

interface ISetIDDeveloper {
	type: EditGameActionTypes.SET_ID_DEVELOPER;
	payload: {
		IDDeveloper: TEditGameSelectedItem;
	};
}

interface IRefetchDevelopers {
	type: EditGameActionTypes.REFETCH_DEVELOPERS;
	payload: {
		needRefetch: boolean;
	};
}

interface ISetFetchedCategories {
	type: EditGameActionTypes.SET_FETCHED_CATEGORIES;
	payload: {
		categories: IItemForGetShortInfo[];
	};
}

interface ISetIDCategory {
	type: EditGameActionTypes.SET_ID_CATEGORY;
	payload: {
		IDCategory: TEditGameSelectedItem;
	};
}

interface IChangeApplicationId {
	type: EditGameActionTypes.CHANGE_APPLICATION_ID;
	payload: {
		applicationId: string;
	};
}

interface IChangeName {
	type: EditGameActionTypes.CHANGE_NAME;
	payload: {
		name: string;
	};
}

interface IChangeVersionCode {
	type: EditGameActionTypes.CHANGE_VERSION_CODE;
	payload: {
		versionCode: string;
	};
}

interface IChangeVersionName {
	type: EditGameActionTypes.CHANGE_VERSION_NAME;
	payload: {
		versionName: string;
	};
}

interface IChangeDescription {
	type: EditGameActionTypes.CHANGE_DESCRIPTION;
	payload: {
		description: string;
	};
}

interface IChangeAppStoreLink {
	type: EditGameActionTypes.CHANGE_APP_STORE_LINK;
	payload: {
		appStoreLink: string;
	};
}

interface IChangePlayMarketLink {
	type: EditGameActionTypes.CHANGE_PLAY_MARKET_LINK;
	payload: {
		playMarketLink: string;
	};
}

interface IChangeFileHash {
	type: EditGameActionTypes.CHANGE_FILE_DATA;
	payload: {
		fileData: IFileData;
	};
}

interface ISetFetchedLanguages {
	type: EditGameActionTypes.SET_FETCHED_LANGUAGES;
	payload: {
		languages: IItemForGetShortInfo[];
	};
}

interface ISetIDsLanguages {
	type: EditGameActionTypes.SET_IDs_LANGUAGES;
	payload: {
		IDsLanguages: TLanguageIDs[];
	};
}

interface ISetDataForSubmit {
	type: EditGameActionTypes.SET_DATA_FOR_SUBMIT;
	payload: {
		data: IEditGame | null;
	};
}

interface IChangeTypeOfModalWindow {
	type: EditGameActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW;
	payload: {
		typeOfModalWindow: TTypeOfEditGameModalWindow;
	};
}

interface IShowErrorModalWithInfo {
	type: EditGameActionTypes.SHOW_ERROR_MODAL_WITH_INFO;
	payload: {
		errorResponseData: IServerError<IGameApplicationIdValidationError> | null;
	};
}

interface ISetTranslationTab {
	type: EditGameActionTypes.SET_TRANSLATION_TAB;
	payload: {
		translationTab: TLanguageIDs | null;
	};
}

interface ISetTranslationFields {
	type: EditGameActionTypes.SET_TRANSLATION_FIELDS;
	payload: {
		updatedTranslations: IGameTranslation[];
	};
}

interface IChangeTranslation {
	type: EditGameActionTypes.CHANGE_TRANSLATION;
	payload: {
		updatedTranslations: IGameTranslation[];
	};
}

interface IResetSelectedControls {
	type: EditGameActionTypes.RESET_FIELDS;
}

export type TEditGameAction =
	| IRefetchData
	| ISetInitialFormData
	| ISetAllData
	| IChangeVisibleForm
	| ISetFile
	| IDeletePhoto
	| ISetFetchedDevelopers
	| ISetIDDeveloper
	| IRefetchDevelopers
	| ISetFetchedCategories
	| ISetIDCategory
	| IChangeApplicationId
	| IChangeName
	| IChangeVersionCode
	| IChangeVersionName
	| IChangeDescription
	| IChangeAppStoreLink
	| IChangePlayMarketLink
	| IChangeFileHash
	| ISetFetchedLanguages
	| ISetIDsLanguages
	| ISetDataForSubmit
	| IChangeTypeOfModalWindow
	| IShowErrorModalWithInfo
	| ISetTranslationTab
	| ISetTranslationFields
	| IChangeTranslation
	| IResetSelectedControls;
